import type { ChangeEvent } from "react";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import type { DateRange } from "@mui/x-date-pickers-pro/models";
import { DateTime } from "luxon";
import { ORDER_STATUSES } from "@/api/order/_models/Order";
import type { OrderStatus } from "@/api/order/_models/Order";
import { ClearFiltersButton } from "@/components/ClearFiltersButton";
import { MultiSelectFilter } from "@/components/MultiSelectFilter";
import { SidePanel } from "@/components/SidePanel";
import { OrderListStatusChip } from "@/features/order/order-list/order-list-status-chip/OrderListStatusChip";
import { OrderListSystemChip } from "@/features/order/order-list/order-list-system-chip/OrderListSystemChip";
import type { OrderListFilterCustomer } from "./toOrderListFilterCustomerList";

/** The order list's filter values, named like GET /orders' params. */
export interface OrderListFilterValues {
  search: string;
  /** Empty means every origin. */
  sources: string[];
  /** Empty means every status. */
  statuses: OrderStatus[];
  /** Customer ids; empty means every customer. */
  customerIds: string[];
  /** Inclusive yyyy-MM-dd day; null leaves that end open. */
  orderDateFrom: string | null;
  orderDateTo: string | null;
}

/** No filter: every order. */
export const ORDER_LIST_EMPTY_FILTER: OrderListFilterValues = {
  search: "",
  sources: [],
  statuses: [],
  customerIds: [],
  orderDateFrom: null,
  orderDateTo: null,
};

/** Whether any filter narrows the list. */
export const isOrderListFilterActive = (filter: OrderListFilterValues): boolean => {
  return (
    filter.search.trim() !== ""
    || filter.sources.length > 0
    || filter.statuses.length > 0
    || filter.customerIds.length > 0
    || filter.orderDateFrom !== null
    || filter.orderDateTo !== null
  );
};

const toPickerDate = (day: string | null): DateTime | null => {
  if (!day) {
    return null;
  }
  return DateTime.fromISO(day);
};

const toFilterDay = (date: DateTime | null): string | null => {
  if (!date || !date.isValid) {
    return null;
  }
  return date.toISODate();
};

const ORDER_SOURCES = ["CIN7", "SHOPIFY"];

interface OrderListFilterPanelProps {
  customers: OrderListFilterCustomer[];
  filter: OrderListFilterValues;
  isOpen: boolean;
  onChange: (filter: OrderListFilterValues) => void;
  onClose: () => void;
}

/** The order list's filters in a side panel: search, order date range, origins, statuses and customers. Changes apply as they're made. */
export const OrderListFilterPanel = (props: OrderListFilterPanelProps) => {
  const { customers, filter, isOpen, onChange, onClose } = props;
  const customerIds = customers.map((customer) => customer.id);
  const customerNameById = new Map(customers.map((customer) => [customer.id, customer.name]));
  const orderDateRange: DateRange<DateTime> = [toPickerDate(filter.orderDateFrom), toPickerDate(filter.orderDateTo)];

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange({ ...filter, search: event.target.value });
  };

  const handleSourcesChange = (sources: string[]) => {
    onChange({ ...filter, sources });
  };

  const handleStatusesChange = (statuses: OrderStatus[]) => {
    onChange({ ...filter, statuses });
  };

  const handleOrderDateRangeChange = (range: DateRange<DateTime>) => {
    onChange({ ...filter, orderDateFrom: toFilterDay(range[0]), orderDateTo: toFilterDay(range[1]) });
  };

  const handleCustomerIdsChange = (nextCustomerIds: string[]) => {
    onChange({ ...filter, customerIds: nextCustomerIds });
  };

  const handleClear = () => {
    onChange(ORDER_LIST_EMPTY_FILTER);
  };

  const renderSource = (system: string) => {
    return <OrderListSystemChip system={system} />;
  };

  const renderStatus = (status: OrderStatus) => {
    return <OrderListStatusChip status={status} />;
  };

  const toCustomerName = (customerId: string): string => {
    return customerNameById.get(customerId) ?? customerId;
  };

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title="Filter orders"
    >
      <Stack
        spacing={2}
        sx={{ flex: 1, mt: 0.5 }}
        useFlexGap
      >
        <TextField
          autoFocus
          fullWidth
          onChange={handleSearchChange}
          placeholder="Order number, customer, SKU…"
          size="small"
          value={filter.search}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        <DateRangePicker
          onChange={handleOrderDateRangeChange}
          value={orderDateRange}
          slotProps={{
            textField: {
              fullWidth: true,
              size: "small",
            },
          }}
        />
        <MultiSelectFilter
          allLabel="All origins"
          onChange={handleSourcesChange}
          options={ORDER_SOURCES}
          renderOption={renderSource}
          value={filter.sources}
        />
        <MultiSelectFilter
          allLabel="All statuses"
          onChange={handleStatusesChange}
          options={ORDER_STATUSES}
          renderOption={renderStatus}
          value={filter.statuses}
        />
        <MultiSelectFilter
          allLabel="All customers"
          getOptionLabel={toCustomerName}
          onChange={handleCustomerIdsChange}
          options={customerIds}
          value={filter.customerIds}
        />
        <ClearFiltersButton
          isActive={isOrderListFilterActive(filter)}
          onClick={handleClear}
        />
      </Stack>
    </SidePanel>
  );
};
